import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export const Header = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [location]);

    const navLinks = [
        { name: 'Home', path: '/' },
        { name: 'About', path: '/about' },
        { name: 'Services', path: '/services' },
        { name: 'Blog', path: '/blog' },
        { name: 'Contact', path: '/contact' },
    ];

    return (
        <header
            data-testid="main-header"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                isScrolled 
                    ? 'header-glass py-3 shadow-lg' 
                    : 'bg-transparent py-5'
            }`}
        >
            <div className="container-hope">
                <nav className="flex items-center justify-between">
         {/* Logo */}
<Link 
    to="/" 
    data-testid="header-logo"
    className="flex items-center gap-3"
>
    <div className={`w-16 h-16 rounded-2xl overflow-hidden flex items-center justify-center transition-all duration-300 ${
        isScrolled
            ? 'bg-white shadow-md'
            : 'bg-white/20 backdrop-blur-md border border-white/30'
    }`}>
        <img
            src="/Hope-Logo.png"
            alt="Hope Accountancy Logo"
            className="w-full h-full object-contain p-2"
        />
    </div>

    <span className={`font-heading font-bold text-xl hidden sm:block ${
        isScrolled ? 'text-hope-emerald' : 'text-white'
    }`}>
        Hope Accountancy
    </span>
</Link>

                    {/* Desktop Navigation */}
                    <div className="hidden lg:flex items-center gap-8">
                        {navLinks.map((link) => (
                            <Link
                                key={link.path}
                                to={link.path}
                                data-testid={`nav-link-${link.name.toLowerCase()}`}
                                className={`font-medium transition-all duration-300 hover:opacity-80 ${
                                    location.pathname === link.path 
                                        ? isScrolled ? 'text-hope-emerald' : 'text-white'
                                        : isScrolled ? 'text-gray-600' : 'text-white/80'
                                }`}
                            >
                                {link.name}
                            </Link>
                        ))}
                    </div>

                    {/* CTA & Mobile Menu */}
                    <div className="flex items-center gap-4">
                        <a
                            href="tel:07440747050"
                            data-testid="header-phone-btn"
                            className={`hidden md:flex items-center gap-2 px-5 py-2.5 rounded-full font-medium transition-all duration-300 ${
                                isScrolled 
                                    ? 'bg-hope-emerald text-white hover:bg-hope-forest' 
                                    : 'bg-white/10 backdrop-blur-md text-white border border-white/30 hover:bg-white/20'
                            }`}
                        >
                            <Phone className="w-4 h-4" />
                            <span>07440 747050</span>
                        </a>

                        {/* Mobile Menu Button */}
                        <button
                            data-testid="mobile-menu-btn"
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className={`lg:hidden p-2 rounded-xl ${
                                isScrolled ? 'text-hope-emerald' : 'text-white'
                            }`}
                        >
                            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                        </button>
                    </div>
                </nav>

                {/* Mobile Menu */}
                <AnimatePresence>
                    {isMobileMenuOpen && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="lg:hidden overflow-hidden"
                        >
                            <div className="py-6 space-y-4">
                                {navLinks.map((link) => (
                                    <Link
                                        key={link.path}
                                        to={link.path}
                                        data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                                        className={`block py-3 px-4 rounded-xl font-medium transition-all ${
                                            location.pathname === link.path
                                                ? 'bg-hope-emerald text-white'
                                                : isScrolled ? 'text-gray-700 hover:bg-hope-light' : 'text-white/90 hover:bg-white/10'
                                        }`}
                                    >
                                        {link.name}
                                    </Link>
                                ))}
                                <a
                                    href="tel:07440747050"
                                    className="flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-hope-emerald text-white font-medium"
                                >
                                    <Phone className="w-4 h-4" />
                                    <span>Call Now</span>
                                </a>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </header>
    );
};
